import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuSeparator, DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Search, Plus, Heart, ChevronRight, User, LogOut, Package, 
  Gavel, Globe, Mail, ShoppingBag, ShoppingCart, Menu, ChevronDown, Bell, Check, Gift, Shield, Sparkles
} from "lucide-react";
import LanguageSwitcher from '@/components/LanguageSwitcher';
import CurrencySelector from '@/components/CurrencySelector';
import AuthForm from '@/components/AuthForm';
import { GetAppButton } from '@/components/AppPromotion';
import { LOGO_URL } from "@/utils/constants";
import api from "@/utils/api";

const Header = ({ user, onLogout, userCurrency, setUserCurrency }) => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [cartCount, setCartCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch unread message count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user) {
        setUnreadCount(0);
        return;
      }
      try {
        const res = await api.get('/messages/unread-count');
        setUnreadCount(res.data.unread_count);
      } catch (error) {
        console.log('Failed to fetch unread count');
      }
    };
    fetchUnreadCount();
    // Poll every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Fetch cart count
  useEffect(() => {
    const fetchCartCount = async () => {
      if (!user) {
        setCartCount(0);
        return;
      }
      try {
        const res = await api.get('/cart/count');
        setCartCount(res.data.count || 0);
      } catch (error) {
        // ignore
      }
    };
    fetchCartCount();
    const interval = setInterval(fetchCartCount, 30000);
    // Listen for cart updates from other pages
    const onCartUpdate = () => fetchCartCount();
    window.addEventListener('cart:updated', onCartUpdate);
    return () => {
      clearInterval(interval);
      window.removeEventListener('cart:updated', onCartUpdate);
    };
  }, [user]);

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) {
        setNotifications([]);
        setNotificationCount(0);
        return;
      }
      try {
        const res = await api.get('/notifications?limit=10');
        setNotifications(res.data.notifications || []);
        setNotificationCount(res.data.unread_count || 0);
      } catch (error) {
        console.log('Failed to fetch notifications');
      }
    };
    fetchNotifications();
    // Poll every 60 seconds
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const handleMarkAllRead = async () => {
    try {
      await api.post('/notifications/mark-all-read');
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setNotificationCount(0);
    } catch (error) {
      console.log('Failed to mark notifications as read');
    }
  };

  const formatNotificationTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  // Fetch categories with subcategories
  const [categories, setCategories] = useState([]);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get('/categories');
        setCategories(res.data);
      } catch (error) {
        console.log('Failed to fetch categories');
      }
    };
    fetchCategories();
  }, []);

  const handleGoogleLogin = async () => {
    try {
      console.log('Starting Google login...');
      const res = await api.get('/auth/google/direct');
      console.log('Google auth response:', res.data);
      if (res.data && res.data.auth_url) {
        window.location.href = res.data.auth_url;
      } else {
        toast.error('Invalid response from server');
      }
    } catch (error) {
      console.error('Google login error:', error);
      toast.error('Failed to connect to Google. Please try again.');
    }
  };

  // All categories for mega menu
  const allCategories = [
    { id: 'electronics', name: 'Electronics', emoji: '💻' },
    { id: 'phones', name: 'Phones & Tablets', emoji: '📱' },
    { id: 'computers', name: 'Computers', emoji: '🖥️' },
    { id: 'fashion', name: 'Fashion', emoji: '👗' },
    { id: 'shoes', name: 'Shoes', emoji: '👟' },
    { id: 'watches', name: 'Watches', emoji: '⌚' },
    { id: 'jewelry', name: 'Jewelry', emoji: '💎' },
    { id: 'collectibles', name: 'Collectibles', emoji: '🏆' },
    { id: 'sports', name: 'Sporting Goods', emoji: '⚽' },
    { id: 'toys', name: 'Toys & Hobbies', emoji: '🧸' },
    { id: 'home', name: 'Home & Garden', emoji: '🏠' },
    { id: 'vehicles', name: 'Motors', emoji: '🚗' },
    { id: 'art', name: 'Art', emoji: '🎨' },
    { id: 'music', name: 'Music', emoji: '🎵' },
    { id: 'books', name: 'Books & Magazines', emoji: '📚' },
    { id: 'cameras', name: 'Cameras & Photo', emoji: '📷' },
    { id: 'gaming', name: 'Video Games', emoji: '🎮' },
    { id: 'health', name: 'Health & Beauty', emoji: '💊' },
    { id: 'baby', name: 'Baby', emoji: '👶' },
    { id: 'pets', name: 'Pet Supplies', emoji: '🐾' },
    { id: 'business', name: 'Business', emoji: '💼' },
    { id: 'antiques', name: 'Antiques', emoji: '🏺' },
    { id: 'crafts', name: 'Crafts', emoji: '✂️' },
    { id: 'garden', name: 'Garden', emoji: '🌱' },
  ];

  const [showCategoryMenu, setShowCategoryMenu] = useState(false);
  const [showAllCategoriesMenu, setShowAllCategoriesMenu] = useState(false);
  const [showNavCategoryMenu, setShowNavCategoryMenu] = useState(false);

  return (
    <>
      <header className={`header-glass ${scrolled ? 'scrolled' : ''}`} data-testid="main-header">
        {/* Top Row - Account Links */}
        <div className="border-b border-slate-200 bg-slate-50">
          <div className="container mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between h-10 text-xs">
              <div className="flex items-center gap-4">
                {user ? (
                  <>
                    <span className="text-slate-600">{t('header.greeting')}, <Link to={`/profile/${user.user_id}`} className="font-semibold text-emerald-600 hover:underline cursor-pointer" data-testid="user-profile-link">{user.name}</Link>!</span>
                    <Link to="/browse?deals=true" className="hover:text-emerald-600 transition-colors">{t('header.dailyDeals')}</Link>
                  </>
                ) : (
                  <>
                    <span className="text-slate-600">{t('header.greeting')}! <button onClick={() => setShowAuthDialog(true)} className="text-emerald-600 hover:underline font-semibold" data-testid="login-btn">{t('header.signIn')}</button> {t('common.or')} <button onClick={() => { setAuthMode('register'); setShowAuthDialog(true); }} className="text-emerald-600 hover:underline font-semibold" data-testid="register-btn">{t('header.register')}</button></span>
                    <Link to="/browse?deals=true" className="hover:text-emerald-600 transition-colors">{t('header.dailyDeals')}</Link>
                  </>
                )}
              </div>
              <div className="flex items-center gap-4">
                {/* Language Switcher */}
                <LanguageSwitcher compact />
                {/* Currency Selector */}
                <CurrencySelector 
                  userCurrency={userCurrency} 
                  setUserCurrency={setUserCurrency}
                  user={user}
                  compact 
                />
                {/* Get App Button */}
                <GetAppButton className="hidden md:flex" />
                <Link to="/sell" className="hover:text-emerald-600 transition-colors font-medium">{t('header.sell')}</Link>
                <DropdownMenu>
                  <DropdownMenuTrigger className="hover:text-emerald-600 transition-colors flex items-center gap-1">
                    My BidZuu <ChevronRight className="h-3 w-3 rotate-90" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {user ? (
                      <>
                        {/* Profile Header in Dropdown */}
                        <div className="px-3 py-2 border-b">
                          <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8">
                              {user.picture ? (
                                <AvatarImage src={user.picture} alt={user.name} />
                              ) : (
                                <AvatarFallback className="bg-emerald-100 text-emerald-700">{user.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                              )}
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold truncate">{user.name}</p>
                              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                            </div>
                          </div>
                          {user.created_at && (
                            <p className="text-[10px] text-muted-foreground mt-1">
                              Member since {new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                            </p>
                          )}
                        </div>
                        <DropdownMenuItem onClick={() => navigate('/account')}>
                          <User className="mr-2 h-4 w-4" /> My Account
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/profile/${user.user_id}`)}>
                          <User className="mr-2 h-4 w-4" /> View Profile
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                          <Package className="mr-2 h-4 w-4" /> Dashboard
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/orders')}>
                          <ShoppingBag className="mr-2 h-4 w-4" /> My Orders
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/dashboard?tab=bids')}>
                          <Gavel className="mr-2 h-4 w-4" /> My Bids
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/dashboard?tab=watchlist')}>
                          <Heart className="mr-2 h-4 w-4" /> Watchlist
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/messages')}>
                          <Mail className="mr-2 h-4 w-4" /> Messages
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/account?tab=buying&section=offers')}>
                          <Gift className="mr-2 h-4 w-4" /> My Offers
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/refer')} data-testid="header-refer-earn-link">
                          <Sparkles className="mr-2 h-4 w-4 text-emerald-500" /> Refer & Earn $5
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate('/sell')}>
                          <Plus className="mr-2 h-4 w-4" /> Sell Item
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={onLogout}>
                          <LogOut className="mr-2 h-4 w-4" /> Sign Out
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <>
                        <DropdownMenuItem onClick={() => setShowAuthDialog(true)}>
                          Sign In
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setAuthMode('register'); setShowAuthDialog(true); }}>
                          Register
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                
                {/* Notifications Bell */}
                {user && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="hover:text-emerald-600 transition-colors relative">
                        <Bell className="h-4 w-4" />
                        {notificationCount > 0 && (
                          <span className="absolute -top-2 -right-2 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                            {notificationCount > 9 ? '9+' : notificationCount}
                          </span>
                        )}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-80">
                      <div className="flex items-center justify-between px-3 py-2 border-b">
                        <span className="font-semibold text-sm">Notifications</span>
                        {notificationCount > 0 && (
                          <button 
                            onClick={handleMarkAllRead}
                            className="text-xs text-emerald-600 hover:underline flex items-center gap-1"
                          >
                            <Check className="h-3 w-3" />
                            Mark all read
                          </button>
                        )}
                      </div>
                      <ScrollArea className="max-h-80">
                        {notifications.length > 0 ? (
                          notifications.map(notif => (
                            <DropdownMenuItem 
                              key={notif.notification_id}
                              className={`flex flex-col items-start gap-1 p-3 cursor-pointer ${!notif.read ? 'bg-emerald-50' : ''}`}
                              onClick={() => notif.link && navigate(notif.link)}
                            >
                              <span className="font-medium text-sm">{notif.title}</span>
                              <span className="text-xs text-gray-500 line-clamp-2">{notif.message}</span>
                              <span className="text-xs text-gray-400">{formatNotificationTime(notif.created_at)}</span>
                            </DropdownMenuItem>
                          ))
                        ) : (
                          <div className="p-4 text-center text-sm text-gray-500">
                            No notifications yet
                          </div>
                        )}
                      </ScrollArea>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                
                <Link to="/messages" className="hover:text-emerald-600 transition-colors relative" data-testid="header-messages-link">
                  <Mail className="h-4 w-4" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-2 -right-2 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Link>

                {user && (
                  <Link to="/cart" className="hover:text-emerald-600 transition-colors relative" data-testid="header-cart-link">
                    <ShoppingCart className="h-4 w-4" />
                    {cartCount > 0 && (
                      <span className="absolute -top-2 -right-2 h-4 w-4 rounded-full bg-emerald-500 text-white text-[10px] flex items-center justify-center" data-testid="header-cart-count">
                        {cartCount > 9 ? '9+' : cartCount}
                      </span>
                    )}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Middle Row - Logo + Search (same row) */}
        <div className="bg-white border-b border-slate-200">
          <div className="container mx-auto px-4 md:px-6">
            <div className="flex items-center gap-4 h-24">
              {/* Logo */}
              <Link to="/" className="flex-shrink-0" data-testid="logo-link">
                <img src={LOGO_URL} alt="BidZuu" style={{height: '90px'}} />
              </Link>
              
              {/* Search Bar */}
              <div className="flex-1 flex">
                <div className="relative flex-1 flex">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground z-10" />
                  <Input 
                    placeholder="Search for anything" 
                    className="pl-12 h-11 text-base rounded-l-lg rounded-r-none border-2 border-r-0 border-slate-300 focus:border-emerald-500 focus:z-10"
                    data-testid="search-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        navigate(`/browse?search=${e.target.value}`);
                      }
                    }}
                  />
                </div>
                
                <Button 
                  className="h-11 px-6 rounded-l-none rounded-r-lg bg-emerald-500 hover:bg-emerald-600" 
                  onClick={() => {
                    const searchVal = document.querySelector('[data-testid="search-input"]')?.value;
                    navigate(`/browse?search=${searchVal || ''}`);
                  }}
                >
                  Search
                </Button>
              </div>
              
              {/* Sell an Item Button */}
              <Link to="/sell" className="hidden lg:flex">
                <Button 
                  data-testid="sell-item-btn"
                  className="h-11 px-6 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg flex items-center gap-2"
                >
                  <Plus className="h-5 w-5" />
                  Sell an Item
                </Button>
              </Link>
              
            </div>
          </div>
        </div>

        {/* Bottom Row - Navigation Links with Shop by Category Mega Menu */}
        <div className="bg-white shadow-sm" style={{ position: 'relative', zIndex: 9999 }}>
          <div className="container mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 h-12 overflow-x-auto scrollbar-hide">
              {/* Shop by Category Mega Menu */}
              <div 
                className="relative"
                onMouseEnter={() => setShowNavCategoryMenu(true)}
                onMouseLeave={() => {
                  setShowNavCategoryMenu(false);
                  setHoveredCategory(null);
                }}
              >
                <button 
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-full whitespace-nowrap transition-colors"
                  data-testid="shop-by-category-btn"
                >
                  <Menu className="h-4 w-4" />
                  Shop by category
                  <ChevronDown className={`h-4 w-4 transition-transform ${showNavCategoryMenu ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Mega Menu Dropdown */}
                {showNavCategoryMenu && (
                  <div className="fixed left-0 top-auto bg-white border rounded-xl shadow-2xl flex min-w-[700px]" style={{ zIndex: 99999, marginTop: '0px' }}>
                    {/* Categories List */}
                    <div className="w-64 border-r py-2 max-h-[500px] overflow-y-auto">
                      {categories.map((cat) => (
                        <div
                          key={cat.id}
                          className={`flex items-center justify-between px-4 py-2.5 cursor-pointer transition-colors ${
                            hoveredCategory === cat.id ? 'bg-emerald-50 text-emerald-700' : 'hover:bg-slate-50'
                          }`}
                          onMouseEnter={() => setHoveredCategory(cat.id)}
                          onClick={() => {
                            navigate(`/browse?category=${cat.id}`);
                            setShowNavCategoryMenu(false);
                          }}
                        >
                          <span className="text-sm font-medium">{cat.name}</span>
                          {cat.subcategories && cat.subcategories.length > 0 && (
                            <ChevronRight className="h-4 w-4 text-slate-400" />
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Subcategories Panel */}
                    <div className="flex-1 p-4 min-h-[400px]">
                      {hoveredCategory ? (
                        <>
                          <h3 className="font-semibold text-lg text-slate-800 mb-3 pb-2 border-b">
                            {categories.find(c => c.id === hoveredCategory)?.name}
                          </h3>
                          <div className="grid grid-cols-2 gap-1">
                            <Link
                              to={`/browse?category=${hoveredCategory}`}
                              className="px-3 py-2 text-sm text-emerald-600 font-medium hover:bg-emerald-50 rounded-lg transition-colors"
                              onClick={() => setShowNavCategoryMenu(false)}
                            >
                              All {categories.find(c => c.id === hoveredCategory)?.name}
                            </Link>
                            {categories.find(c => c.id === hoveredCategory)?.subcategories?.map((sub) => (
                              <Link
                                key={sub.id}
                                to={`/browse?category=${hoveredCategory}&subcategory=${sub.id}`}
                                className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 rounded-lg transition-colors"
                                onClick={() => setShowNavCategoryMenu(false)}
                              >
                                {sub.name}
                              </Link>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center justify-center h-full text-slate-400">
                          <p>Hover over a category to see subcategories</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <Link to="/live" className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-full whitespace-nowrap transition-colors">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </span>
                BidZuu Live
              </Link>
              <Link to="/community" className="px-4 py-2 text-sm font-medium text-emerald-600 hover:bg-emerald-50 rounded-full whitespace-nowrap transition-colors flex items-center gap-1">
                💬 Community
              </Link>
              <Link to="/map" className="px-4 py-2 text-sm font-medium text-emerald-600 hover:bg-emerald-50 rounded-full whitespace-nowrap transition-colors flex items-center gap-1">
                <Globe className="h-4 w-4" /> Map
              </Link>
              
              {/* Spacer to push Get Inspection to the right */}
              <div className="flex-1"></div>
              
              {/* Get Inspection - Right side with blue color and shield emoji */}
              <Link to="/inspectors" className="px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-full whitespace-nowrap transition-colors flex items-center gap-1.5 border border-blue-200 bg-blue-50/50">
                🛡️ Get Inspection
              </Link>
            </div>
          </div>
        </div>
      </header>

      <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center font-outfit text-2xl">
              {authMode === "login" ? "Welcome Back" : "Create Account"}
            </DialogTitle>
          </DialogHeader>
          <AuthForm 
            mode={authMode} 
            onSuccess={(userData, token) => {
              setShowAuthDialog(false);
              window.location.reload();
            }}
            onGoogleLogin={handleGoogleLogin}
            onSwitchMode={() => setAuthMode(authMode === "login" ? "register" : "login")}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Header;
